import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Col, 
  // Container, 
  Row,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react';
import { 
  getUsageAnalytics, 
  getCustomerLicenseUsage, 
  getChangeLogs,
  getCustomerLicenseMetrics
} from '../../services/api';
import classnames from 'classnames';
import ReactApexChart from 'react-apexcharts';

// Using ApexCharts for the license bar chart

const UsageAnalyticsList = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('1');
  
  // Loading states for each tab
  const [loading, setLoading] = useState({
    usage: true,
    customer: true,
    changelog: true
  });
  
  // Usage analytics state
  const [usageData, setUsageData] = useState([]);
  const [usageTotalRows, setUsageTotalRows] = useState(0);
  const [usagePerPage, setUsagePerPage] = useState(10);
  const [usageCurrentPage, setUsageCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Customer license usage state
  const [customerUsageData, setCustomerUsageData] = useState([]);
  const [customerTotalRows, setCustomerTotalRows] = useState(0);
  const [customerPerPage, setCustomerPerPage] = useState(10);
  // eslint-disable-next-line
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const [customerLicenseMetrics, setCustomerLicenseMetrics] = useState([]);
  
  // Change logs state
  const [changeLogs, setChangeLogs] = useState([]);
  const [changeLogsPerPage] = useState(10);
  // eslint-disable-next-line
  const [logTypeFilter, setLogTypeFilter] = useState('all');
  // eslint-disable-next-line
  const [changeLogsPage, setChangeLogsPage] = useState(1);

  // Columns configuration for the data table
  const columns = [
    {
      name: 'Date',
      selector: row => row.date,
      sortable: true,
    },
    {
      name: 'Active Users',
      selector: row => row.activeUsers,
      sortable: true,
    },
    {
      name: 'Page Views',
      selector: row => row.pageViews,
      sortable: true,
    },
    {
      name: 'Sessions',
      selector: row => row.sessions,
      sortable: true,
    },
    {
      name: 'Avg. Session Duration',
      selector: row => row.avgSessionDuration,
      sortable: true,
    },
    {
      name: 'Used Licenses',
      selector: row => row.licenseMetrics.licensedUsers,
      sortable: true,
      cell: row => (
        <div>
          <div>{row.licenseMetrics.licensedUsers}</div>
          <small className="text-muted">
            {row.licenseMetrics.licenseUtilization} utilization
          </small>
        </div>
      ),
    },
    {
      name: 'License Metrics',
      selector: row => row.licenseMetrics.totalLicenses,
      sortable: false,
      cell: row => (
        <div className="text-center">
          <div className="d-flex flex-column">
            <small className="text-muted">Max: {row.licenseMetrics.totalLicenses}</small>
            <small className="text-muted">Active: {row.licenseMetrics.activeLicensedUsers}</small>
            <small className="text-muted">Users: {row.licenseMetrics.totalUsers}</small>
          </div>
        </div>
      ),
    },
  ];

  // Aggregated per-customer totals table columns (to match other DataTables)
  const aggregatedLicenseColumns = [
    {
      name: 'Customer',
      selector: row => row.customer,
      sortable: true,
      wrap: true,
    },
    {
      name: 'Total Licenses',
      selector: row => row.total_licenses,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total Max Employees',
      selector: row => row.total_max_employees,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total Active Employees',
      selector: row => row.total_active_employees,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total Max Users',
      selector: row => row.total_max_users,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total Active Users',
      selector: row => row.total_active_users,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total Max Companies',
      selector: row => row.total_max_companies,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total Active Companies',
      selector: row => row.total_active_companies,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total Max MAC Changes',
      selector: row => row.total_max_mac_address_change,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Total MAC Change Count',
      selector: row => row.total_mac_change_count,
      sortable: true,
      right: true,
      style: { textAlign: 'right' },
    },
  ];

  // Helpers and config for the bar chart in Usage tab (ApexCharts)
  const parseNumber = (val) => {
    if (val == null) return 0;
    if (typeof val === 'number') return val;
    const n = Number(String(val).replace(/,/g, ''));
    return Number.isNaN(n) ? 0 : n;
  };

  const barChartLabels = ['Max Licenses', 'Used Licenses', 'Active Licensed Users', 'Total Users', 'Active Users'];

  const barChartSeriesTwo = React.useMemo(() => {
    if (!usageData || usageData.length === 0) return [];
    const lm = usageData[0]?.licenseMetrics || {};
    return [
      {
        name: 'Count',
        data: [
          parseNumber(lm.totalLicenses),
          parseNumber(lm.licensedUsers),
          parseNumber(lm.activeLicensedUsers),
          parseNumber(lm.totalUsers),
          parseNumber(lm.activeUsersCount),
        ],
      },
    ];
  }, [usageData]);

  const barChartOptionsTwo = React.useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: {
      bar: { columnWidth: '45%', distributed: true, borderRadius: 4 }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: barChartLabels,
      labels: { style: { colors: '#6c757d' } }
    },
    yaxis: {
      labels: { style: { colors: '#6c757d' } }
    },
    colors: ['#6c5ce7', '#00b894', '#0984e3', '#fdcb6e', '#d63031'],
    tooltip: { y: { formatter: (val) => `${val}` } },
  }), []);

  // Fetch usage analytics data
  const fetchUsageData = useCallback(async (page, size = usagePerPage, sortField = sortColumn, sortDir = sortDirection) => {
    setLoading(prev => ({ ...prev, usage: true }));
    try {
      const result = await getUsageAnalytics({
        page,
        perPage: size,
        sortField,
        sortDir: sortDir === 'asc' ? 'asc' : 'desc'
      });
  
      const formatNumber = (num) => num != null ? Number(num).toLocaleString() : '0';
      
      const formattedData = result.data.map(item => {
        const licenseMetrics = item.license_metrics || {};
        return {
          id: item.record_date, // Use record_date as unique ID
          date: new Date(item.record_date).toISOString().split('T')[0],
          activeUsers: formatNumber(item.active_users),
          pageViews: formatNumber(item.page_views),
          sessions: formatNumber(item.sessions),
          avgSessionDuration: formatDuration(item.avg_session_duration_seconds),
          licenseMetrics: {
            totalLicenses: formatNumber(licenseMetrics.total_licenses),
            licensedUsers: formatNumber(licenseMetrics.licensed_users),
            activeLicensedUsers: formatNumber(licenseMetrics.active_licensed_users),
            licenseUtilization: licenseMetrics.license_utilization_percentage != null 
              ? `${Math.round(licenseMetrics.license_utilization_percentage)}%` 
              : '0%',
            totalUsers: formatNumber(licenseMetrics.total_users),
            activeUsersCount: formatNumber(licenseMetrics.active_users_count)
          }
        };
      });
      
      setUsageData(formattedData);
      setUsageTotalRows(result.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching usage analytics:', error);
      // Optionally show error to user
      // setError('Failed to load usage analytics data');
    } finally {
      setLoading(prev => ({ ...prev, usage: false }));
    }
  }, [usagePerPage, sortColumn, sortDirection]);
  

  // Fetch customer license usage data
  const fetchCustomerUsageData = useCallback(async (page = 1, size = customerPerPage) => {
    setLoading(prev => ({ ...prev, customer: true }));
    try {
      const result = await getCustomerLicenseUsage({
        page,
        perPage: size
      });

      
      // Transform the data to match the expected format
      const formattedData = result.data.map(item => ({
        id: item.customer_id || Math.random().toString(36).substr(2, 9),
        customerName: item.customer_name || 'N/A',
        licenseType: item.primary_licenses > 0 ? 'Primary' : 'Standard',
        totalLicenses: item.total_licenses || 0,
        usedLicenses: item.active_licenses || 0,
        usagePercentage: calculateUsagePercentage(
          item.active_licenses || 0,
          item.total_licenses || 1
        ),
        status: item.latest_expiry_date && new Date(item.latest_expiry_date) > new Date() ? 'Active' : 'Inactive'
      }));
      
      setCustomerUsageData(formattedData);
      setCustomerTotalRows(result.pagination?.total || formattedData.length);

      // Fetch aggregated per-customer totals
      try {
        const totals = await getCustomerLicenseMetrics();
        setCustomerLicenseMetrics(Array.isArray(totals) ? totals : []);
      } catch (e) {
        console.error('Error fetching customer license metrics:', e);
      }
    } catch (error) {
      console.error('Error fetching customer license usage:', error);
    } finally {
      setLoading(prev => ({ ...prev, customer: false }));
    }
  }, [customerPerPage]);

  // (Removed fetchValidationTrends and related state)

  // Fetch change logs
  const fetchChangeLogs = useCallback(async (page = 1, size = changeLogsPerPage, type = 'all') => {
    setLoading(prev => ({ ...prev, changelog: true }));
    try {
      const result = await getChangeLogs({
        page,
        perPage: size,
        type: type === 'all' ? undefined : type
      });

      
      setChangeLogs(result.data);
    } catch (error) {
      console.error('Error fetching change logs:', error);
    } finally {
      setLoading(prev => ({ ...prev, changelog: false }));
    }
  }, [changeLogsPerPage]);

  // Initial data fetch for the first tab
  useEffect(() => {
    fetchUsageData(usageCurrentPage);
  }, [usageCurrentPage, fetchUsageData]);

  // Comment out unused variables to fix lint warnings
  // These can be safely removed if not needed
  // const unusedVars = {
  //   Container,
  //   customerCurrentPage,
  //   handleLogTypeFilterChange,
  //   logTypeFilter,
  //   changeLogsPage
  // };
  // console.log('Unused variables:', Object.keys(unusedVars).join(', '));

  // Helper function to calculate usage percentage
  const calculateUsagePercentage = (used, total) => {
    if (!total) return 0;
    return Math.round((used / total) * 100);
  };

  // Helper function to format duration in seconds to 'Xm Ys' format
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    
    // Fetch data based on the selected tab
    if (tab === '1') {
      fetchUsageData(1);
    } else if (tab === '2') {
      fetchCustomerUsageData(1);
    } else if (tab === '4') {
      fetchChangeLogs(1, changeLogsPerPage, 'all');
    }
  }, [fetchUsageData, fetchCustomerUsageData, fetchChangeLogs, changeLogsPerPage]);

  const toggleTab = useCallback(tab => {
    if (activeTab !== tab) {
      handleTabChange(tab);
      
      // Load data when tab changes if not loaded
      switch(tab) {
        case '1':
          if (usageData.length === 0) fetchUsageData(1);
          break;
        case '2':
          if (customerUsageData.length === 0) fetchCustomerUsageData(1);
          break;
        case '4':
          if (changeLogs.length === 0) fetchChangeLogs(1, changeLogsPerPage, 'all');
          break;
        default:
          break;
      }
    }
  }, [activeTab, handleTabChange, usageData.length, customerUsageData.length, fetchUsageData, fetchCustomerUsageData, fetchChangeLogs, changeLogsPerPage, changeLogs.length]);

  // Handle page changes for each section
  const handleUsagePageChange = useCallback(page => {
    setUsageCurrentPage(page);
    fetchUsageData(page);
  }, [fetchUsageData]);

  const handleCustomerPageChange = useCallback(page => {
    setCustomerCurrentPage(page);
    fetchCustomerUsageData(page);
  }, [fetchCustomerUsageData]);

  // Handle per row changes
  const handleUsagePerRowsChange = useCallback((newPerPage, page) => {
    setUsagePerPage(newPerPage);
    fetchUsageData(page, newPerPage);
  }, [fetchUsageData]);

  const handleCustomerPerRowsChange = useCallback((newPerPage, page) => {
    setCustomerPerPage(newPerPage);
    fetchCustomerUsageData(page, newPerPage);
  }, [fetchCustomerUsageData]);

  // Handle sort for usage data
  const handleSort = useCallback((column, sortDirection) => {
    setSortColumn(column.selector);
    setSortDirection(sortDirection);
    fetchUsageData(usageCurrentPage, usagePerPage, column.selector, sortDirection);
  }, [fetchUsageData, usageCurrentPage, usagePerPage]);

  // Prepare data for CSV export
  const getCsvData = () => {
    switch(activeTab) {
      case '1': // Usage Analytics
        return usageData.map(item => ({
          'Date': item.date,
          'Active Users': item.activeUsers,
          'Page Views': item.pageViews,
          'Sessions': item.sessions,
          'Avg. Session Duration': item.avgSessionDuration,
        }));
      case '2': // Customer License Usage
        return customerUsageData.map(item => ({
          'Customer': item.customerName,
          'License Type': item.licenseType,
          'Max Licenses': item.totalLicenses,
          'Used Licenses': item.usedLicenses,
          'Usage %': `${item.usagePercentage}%`,
          'Status': item.status
        }));
      case '4': // Change Logs
        return changeLogs.map(item => ({
          'Timestamp': new Date(item.timestamp).toLocaleString(),
          'Type': item.type,
          'Action': item.action,
          'Description': item.description,
          'User': item.user,
          'IP Address': item.ipAddress
        }));
      default:
        return [];
    }
  };
  
  const getExportFilename = () => {
    const prefix = 'analytics-export';
    const date = new Date().toISOString().split('T')[0];
    
    switch(activeTab) {
      case '1': return `${prefix}-usage-${date}.csv`;
      case '2': return `${prefix}-customer-licenses-${date}.csv`;
      case '4': return `${prefix}-change-logs-${date}.csv`;
      default: return `${prefix}-${date}.csv`;
    }
  };

  // Customer license usage columns
  const customerLicenseColumns = [
    {
      name: 'Customer',
      selector: row => row.customerName,
      sortable: true,
    },
    {
      name: 'License Type',
      selector: row => row.licenseType,
      sortable: true,
    },
    {
      name: 'Max Licenses',
      selector: row => row.totalLicenses,
      sortable: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Used Licenses',
      selector: row => row.usedLicenses,
      sortable: true,
      style: { textAlign: 'right' },
    },
    {
      name: 'Usage %',
      selector: row => row.usagePercentage,
      sortable: true,
      style: { textAlign: 'right' },
      cell: row => (
        <div className="d-flex align-items-center">
          <div className="progress flex-grow-1 me-2" style={{ height: '6px' }}>
            <div 
              className={`progress-bar ${row.usagePercentage > 90 ? 'bg-danger' : row.usagePercentage > 70 ? 'bg-warning' : 'bg-success'}`} 
              role="progressbar" 
              style={{ width: `${Math.min(100, row.usagePercentage)}%` }}
            ></div>
          </div>
          <span>{row.usagePercentage}%</span>
        </div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      cell: row => (
        <Badge color={row.status === 'Active' ? 'success' : 'secondary'}>
          {row.status}
        </Badge>
      ),
    },
  ];

  return (
    <Card title="Advanced Analytics Dashboard">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Advanced Analytics Dashboard</h6>
        <CSVLink 
          data={getCsvData()} 
          filename={getExportFilename()}
          className="btn btn-primary"
          target="_blank"
        >
          <Icon icon="mdi:download" className="me-1" />
          Export to CSV
        </CSVLink>
      </CardHeader>
      <CardBody className="p-0">
        <Nav tabs className="px-0 pt-2 w-100" style={{ borderBottom: '1px solid #dee2e6' }}>
          <NavItem className="flex-grow-1 text-center">
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => toggleTab('1')}
              style={{
                cursor: 'pointer',
                padding: '0.75rem 1rem',
                border: 'none',
                background: 'none',
                color: activeTab === '1' ? '#4361ee' : '#6c757d',
                fontWeight: activeTab === '1' ? 600 : 500,
                borderBottom: activeTab === '1' ? '3px solid #4361ee' : '3px solid transparent',
                transition: 'all 0.2s ease-in-out',
                marginBottom: '-1px'
              }}
            >
              <Icon icon="mdi:chart-line" className="me-1" />
              Usage Analytics
            </NavLink>
          </NavItem>
          <NavItem className="flex-grow-1 text-center">
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              onClick={() => toggleTab('2')}
              style={{
                cursor: 'pointer',
                padding: '0.75rem 1rem',
                border: 'none',
                background: 'none',
                color: activeTab === '2' ? '#4361ee' : '#6c757d',
                fontWeight: activeTab === '2' ? 600 : 500,
                borderBottom: activeTab === '2' ? '3px solid #4361ee' : '3px solid transparent',
                transition: 'all 0.2s ease-in-out',
                marginBottom: '-1px'
              }}
            >
              <Icon icon="mdi:account-group" className="me-1" />
              Customer Licenses
            </NavLink>
          </NavItem>
          {/* Validation Trends tab removed */}
          {/* <NavItem className="flex-grow-1 text-center">
            <NavLink
              className={classnames({ active: activeTab === '4' })}
              onClick={() => toggleTab('4')}
              style={{
                cursor: 'pointer',
                padding: '0.75rem 1rem',
                border: 'none',
                background: 'none',
                color: activeTab === '4' ? '#4361ee' : '#6c757d',
                fontWeight: activeTab === '4' ? 600 : 500,
                borderBottom: activeTab === '4' ? '3px solid #4361ee' : '3px solid transparent',
                transition: 'all 0.2s ease-in-out',
                marginBottom: '-1px'
              }}
            >
              <Icon icon="mdi:history" className="me-1" />
              Change Logs
            </NavLink>
          </NavItem> */}
        </Nav>
        
        <TabContent activeTab={activeTab} className="p-3">
          {/* Tab 1: Usage Analytics */}
          <TabPane tabId="1">
            {/* License Usage Summary Cards */}
            {usageData.length > 0 && (
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <CardBody>
                      <h4 className="text-primary mb-1">
                        {usageData[0]?.licenseMetrics?.totalLicenses || '0'}
                      </h4>
                      <small className="text-muted">Max Licenses</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <CardBody>
                      <h4 className="text-success mb-1">
                        {usageData[0]?.licenseMetrics?.licensedUsers || '0'}
                      </h4>
                      <small className="text-muted">Used Licenses</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <CardBody>
                      <h4 className="text-info mb-1">
                        {usageData[0]?.licenseMetrics?.activeLicensedUsers || '0'}
                      </h4>
                      <small className="text-muted">Active Licensed Users</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <CardBody>
                      <h4 className="text-warning mb-1">
                        {usageData[0]?.licenseMetrics?.licenseUtilization || '0%'}
                      </h4>
                      <small className="text-muted">License Utilization</small>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
            
            <div className="table-responsive">
              {barChartSeriesTwo && barChartSeriesTwo.length > 0 && (
                <div style={{ height: '310px' }}>
                  <ReactApexChart options={barChartOptionsTwo} series={barChartSeriesTwo} type="bar" height={310} />
                </div>
              )}
            </div>
          </TabPane>
          
          {/* Tab 2: Customer License Usage */}
          <TabPane tabId="2">
            <div className="">
              <DataTable
                columns={customerLicenseColumns}
                data={customerUsageData}
                progressPending={loading.customer}
                pagination
                paginationServer
                paginationTotalRows={customerTotalRows}
                onChangeRowsPerPage={handleCustomerPerRowsChange}
                onChangePage={handleCustomerPageChange}
                defaultSortFieldId="customerName"
                defaultSortAsc={true}
                striped
                highlightOnHover
                className="table-hover"
              />
            </div>

            {customerLicenseMetrics && customerLicenseMetrics.length > 0 && (
              <div className="mt-4">
                <h6 className="mb-3">Aggregated License Totals</h6>
                <div className="table-responsive">
                  <DataTable
                    columns={aggregatedLicenseColumns}
                    data={customerLicenseMetrics.map((item, idx) => ({
                      id: idx,
                      customer: item.customer,
                      total_licenses: item.license_metrics?.total_licenses ?? 0,
                      total_max_employees: item.license_metrics?.total_max_employees ?? 0,
                      total_active_employees: item.license_metrics?.total_active_employees ?? 0,
                      total_max_users: item.license_metrics?.total_max_users ?? 0,
                      total_active_users: item.license_metrics?.total_active_users ?? 0,
                      total_max_companies: item.license_metrics?.total_max_companies ?? 0,
                      total_active_companies: item.license_metrics?.total_active_companies ?? 0,
                      total_max_mac_address_change: item.license_metrics?.total_max_mac_address_change ?? 0,
                      total_mac_change_count: item.license_metrics?.total_mac_change_count ?? 0,
                    }))}
                    dense
                    striped
                    highlightOnHover
                    pagination
                    paginationPerPage={10}
                    className="table-hover"
                  />
                </div>
              </div>
            )}
          </TabPane>
          
          {/* Validation Trends content removed */}
          
          {/* Tab 4: Change Logs */}
          {/* <TabPane tabId="4">
            {loading.changelog ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading change logs...</p>
              </div>
            ) : (
              <div className="change-logs">
                <div className="mb-3">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-outline-primary ${logTypeFilter === 'all' ? 'active' : ''}`}
                      onClick={() => handleLogTypeFilterChange('all')}
                    >
                      All Logs
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-success ${logTypeFilter === 'create' ? 'active' : ''}`}
                      onClick={() => handleLogTypeFilterChange('create')}
                    >
                      <Icon icon="mdi:plus-circle-outline" className="me-1" />
                      Additions
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-warning ${logTypeFilter === 'update' ? 'active' : ''}`}
                      onClick={() => handleLogTypeFilterChange('update')}
                    >
                      <Icon icon="mdi:pencil-outline" className="me-1" />
                      Changes
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-danger ${logTypeFilter === 'delete' ? 'active' : ''}`}
                      onClick={() => handleLogTypeFilterChange('delete')}
                    >
                      <Icon icon="mdi:delete-outline" className="me-1" />
                      Revocations
                    </button>
                  </div>
                </div>

                <Card>
                  <CardBody className="p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Timestamp</th>
                            <th>Type</th>
                            <th>Action</th>
                            <th>Description</th>
                            <th>User</th>
                            <th>IP Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {changeLogs.length > 0 ? (
                            changeLogs.map((log, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="fw-medium">
                                      {new Date(log.timestamp).toLocaleDateString()}
                                    </span>
                                    <small className="text-muted">
                                      {new Date(log.timestamp).toLocaleTimeString()}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <Badge 
                                    color={
                                      log.type === 'create' ? 'success' : 
                                      log.type === 'update' ? 'warning' : 'danger'
                                    }
                                    className="text-uppercase"
                                  >
                                    {log.type}
                                  </Badge>
                                </td>
                                <td>{log.action}</td>
                                <td>{log.description}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="avatar-xs me-2">
                                      <div className="avatar-title rounded-circle bg-light text-dark">
                                        {log.user ? log.user.charAt(0).toUpperCase() : 'U'}
                                      </div>
                                    </div>
                                    <span>{log.user || 'System'}</span>
                                  </div>
                                </td>
                                <td>
                                  <code>{log.ipAddress || 'N/A'}</code>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <div className="text-muted">
                                  <Icon icon="mdi:inbox-remove-outline" width={48} height={48} />
                                  <p className="mt-2 mb-0">No change logs found</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                  
                  {changeLogsTotal > 0 && (
                    <div className="card-footer d-flex justify-content-between align-items-center">
                      <div>
                        Showing {Math.min((changeLogsPage - 1) * changeLogsPerPage + 1, changeLogsTotal)} 
                        to {Math.min(changeLogsPage * changeLogsPerPage, changeLogsTotal)} 
                        of {changeLogsTotal} entries
                      </div>
                      <div className="btn-group">
                        <button 
                          className="btn btn-outline-secondary btn-sm" 
                          onClick={() => handleChangeLogsPageChange(changeLogsPage - 1)}
                          disabled={changeLogsPage === 1}
                        >
                          Previous
                        </button>
                        <button 
                          className="btn btn-outline-secondary btn-sm" 
                          onClick={() => handleChangeLogsPageChange(changeLogsPage + 1)}
                          disabled={changeLogsPage * changeLogsPerPage >= changeLogsTotal}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </Card>
                
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      <Icon icon="mdi:information-outline" className="me-1" />
                      Showing {changeLogsPerPage} entries per page
                    </div>
                    <div className="d-flex align-items-center">
                      <label htmlFor="changeLogsPerPage" className="form-label mb-0 me-2">
                        Rows per page:
                      </label>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={changeLogsPerPage}
                        onChange={(e) => handleChangeLogsPerRowsChange(Number(e.target.value), 1)}
                        id="changeLogsPerPage"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabPane> */}
        </TabContent>
      </CardBody>
    </Card>
  );
};

export default UsageAnalyticsList;
